import { useEffect, useRef } from 'react';

/**
 * Hook to handle lobby WebSocket message routing and notifications.
 * Extracted from App.js to reduce component complexity.
 */
export const useLobbyMessages = ({
  lobbyConnected,
  addMessageHandler,
  addGlobalNotification,
  notifyMatchFound,
  notifyFriendRequest,
  notifyMessage,
  notifySpectatorRequest,
  notifyRejoinRequest,
  playSound,
  handleGameStartRef,
  setShowMultiplayerLobby
}) => {
  useEffect(() => {
    if (!lobbyConnected || !addMessageHandler) return;
    
    const handleGlobalLobbyMessage = (data) => {
      if (data.type === 'room_joined' || data.type === 'player_joined') {
        addGlobalNotification(
          'room_joined',
          data.game_id,
          data.room_name,
          `${data.joiner_name || data.user_name || 'Someone'} joined your room!`
        );
        notifyMatchFound(data.room_name);
      } else if (data.type === 'room_ready') {
        addGlobalNotification(
          'game_ready',
          data.game_id,
          data.room_name || 'Game Room',
          'Game is ready to start!'
        );
        notifyMatchFound(data.room_name || 'Game Room');
      } else if (data.type === 'spectator_joined') {
        addGlobalNotification(
          'spectator_joined',
          data.game_id,
          data.room_name || 'Your Game',
          `${data.spectator_name || 'Someone'} is watching`
        );
      } else if (data.type === 'lobby_message') {
        addGlobalNotification(
          'lobby_message',
          null,
          'Lobby Chat',
          `${data.user_name || 'Someone'}: ${data.message?.substring(0, 50) || 'sent a message'}`
        );
      } else if (data.type === 'friend_request_accepted') {
        addGlobalNotification(
          'friend_request_accepted',
          null,
          data.accepter_name || 'Friend',
          data.message || `${data.accepter_name} accepted your friend request!`
        );
      } else if (data.type === 'friend_request_declined') {
        addGlobalNotification(
          'friend_request_declined',
          null,
          data.decliner_name || 'User',
          data.message || `${data.decliner_name} declined your friend request`
        );
      } else if (data.type === 'friend_request_received') {
        addGlobalNotification(
          'friend_request_received',
          null,
          data.sender_name || 'Someone',
          `${data.sender_name || 'Someone'} sent you a friend request`
        );
        notifyFriendRequest(data.sender_name || 'Someone');
      } else if (data.type === 'new_message') {
        addGlobalNotification(
          'chat_message',
          null,
          data.sender_name || 'Friend',
          `${data.sender_name || 'Friend'}: ${data.message?.substring(0, 50) || 'sent a message'}`
        );
        notifyMessage(data.sender_name || 'Friend', data.message?.substring(0, 50) || 'sent a message');
      } else if (data.type === 'spectator_request') {
        addGlobalNotification(
          'spectator_request',
          data.game_id,
          data.user_name || 'Someone',
          `${data.user_name || 'Someone'} wants to spectate your game`,
          data.user_id
        );
        notifySpectatorRequest(data.user_name || 'Someone');
      } else if (data.type === 'rejoin_request') {
        addGlobalNotification(
          'rejoin_request',
          data.game_id,
          data.room_name || 'Room',
          `${data.requester_name || 'Someone'} wants to rejoin your room`,
          data.requester_id
        );
        notifyRejoinRequest(data.requester_name || 'Someone', data.room_name || 'Room');
      } else if (data.type === 'rejoin_approved') {
        addGlobalNotification(
          'rejoin_approved',
          data.game_id,
          data.room_name || 'Room',
          data.message || 'Your rejoin request was approved!'
        );
        playSound('matchFound');
        
        if (data.host_settings) {
          window.chessKingdomHostSettings = data.host_settings;
        }
        
        if (data.game_id && handleGameStartRef.current) {
          setTimeout(() => {
            setShowMultiplayerLobby(false);
            handleGameStartRef.current(data.game_id, 'multiplayer', data.room_name || 'Game Room');
          }, 1500);
        }
      } else if (data.type === 'rejoin_denied') {
        addGlobalNotification(
          'rejoin_denied',
          data.game_id,
          data.room_name || 'Room',
          data.message || 'Your rejoin request was denied'
        );
      }
    };
    
    const unsubscribe = addMessageHandler(handleGlobalLobbyMessage);
    return () => unsubscribe();
  }, [lobbyConnected, addMessageHandler, addGlobalNotification, notifyMatchFound, notifyFriendRequest, notifyMessage, notifySpectatorRequest, notifyRejoinRequest, playSound, handleGameStartRef, setShowMultiplayerLobby]);
};

export default useLobbyMessages;
